import { Banner } from "@components/Banner";
import { NotFound } from "@components/HttpErrors";
import { ILSFooter } from "@components/ILSFooter";
import { ILSMenu } from "@components/ILSMenu";
import { Notifications } from "@components/Notifications";
import { getStaticPagesRoutes } from "@config";
import { DocumentRequestForm } from "@pages/frontsite/DocumentRequests";
import { Dashboard } from "@pages/frontsite/Dashboard";
import { DocumentDetails } from "@pages/frontsite/Documents";
import { ErrorsPage } from "@pages/frontsite/ErrorsPage";
import { Home } from "@pages/frontsite/Home";
import LiteratureSearch from "@pages/frontsite/Literature/LiteratureSearch/LiteratureSearch";
import { OpeningHours } from "@pages/frontsite/OpeningHours";
import { SelfCheckout } from "@pages/frontsite/SelfCheckout";
import { PatronProfile } from "@pages/frontsite/PatronProfile";
import { SeriesDetails } from "@pages/frontsite/Series";
import { StaticPage } from "@pages/frontsite/StaticPage";
import { FrontSiteRoutes } from "@routes/frontsite/frontsiteUrls";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Route, Switch } from "react-router-dom";
import { invenioConfig } from "@config";
import { AuthenticationGuard } from "@authentication/components/AuthenticationGuard";
import { UnAuthorized } from "@authentication/components/UnAuthorized";
import { Feedback } from "@pages/frontsite/Feedback";
import { Container } from "semantic-ui-react";
import Overridable from "react-overridable";
import SlipForm from "@pages/frontsite/Documents/DocumentDetails/LoanRequestForm/SlipForm/SlipForm";
import { FrequentlyAskedQuestions } from "@pages/frontsite/UserGuides/FAQs";
import { SpecialCollectionDetails } from "@pages/frontsite/SpecialCollections/SpecialCollectionDetails";
import LibraryIdAttendance from "@pages/backoffice/Attendance/Attendance";
import { ProtectedRoute } from "@authentication/components/AdminAccess/AdminAccess";

export default class FrontSite extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentUser: null,
      isLoadingUser: false,
    };
    // Add mounted flag to track component lifecycle
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchCurrentUser();
  }

  componentWillUnmount() {
    // Set mounted flag to false when component unmounts
    this._isMounted = false;
  }

  // Fetch current user data
  fetchCurrentUser = async () => {
    this.setState({ isLoadingUser: true });

    try {
      const response = await fetch("/api/me", {
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const userData = await response.json();
        // Only update state if component is still mounted
        if (this._isMounted) {
          this.setState({
            currentUser: userData,
            isLoadingUser: false,
          });
        }
      } else {
        if (this._isMounted) {
          this.setState({
            currentUser: null,
            isLoadingUser: false,
          });
        }
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({
          currentUser: null,
          isLoadingUser: false,
        });
      }
    }
  };

  renderCustomStaticPages = () => {
    const { customStaticPages } = this.props;
    customStaticPages();
  };

  render() {
    const staticPagesRoutes = getStaticPagesRoutes();
    const { currentUser, isLoadingUser } = this.state;

    return (
      <div className="frontsite">
        <ILSMenu />
        <Notifications className="compact" />
        <Container fluid className="fs-content">
          <Overridable id="FrontSite.extras">
            <Banner />
          </Overridable>
          <Switch>
            <Route
              exact
              path={FrontSiteRoutes.patronProfile}
              component={PatronProfile}
            />
            <AuthenticationGuard
              exact
              path={FrontSiteRoutes.dashboard}
              authorizedComponent={Dashboard}
              unAuthorizedComponent={UnAuthorized}
            />

            {/* OPAC ROUTES - Use ProtectedRoute to block admins, allow everyone else */}
            <ProtectedRoute
              exact
              path={FrontSiteRoutes.home}
              render={(props) => <Home {...props} {...this.props} />}
            />
            <ProtectedRoute
              exact
              path={FrontSiteRoutes.documentsList}
              render={(props) => (
                <LiteratureSearch
                  {...props}
                  user={currentUser}
                  isLoadingUser={isLoadingUser}
                />
              )}
            />
            <ProtectedRoute
              exact
              path={FrontSiteRoutes.documentDetails}
              component={DocumentDetails}
            />
            <ProtectedRoute
              exact
              path={FrontSiteRoutes.seriesDetails}
              component={SeriesDetails}
            />
            <ProtectedRoute
              exact
              path={FrontSiteRoutes.documentRequestForm}
              component={DocumentRequestForm}
            />
            <ProtectedRoute
              exact
              path={FrontSiteRoutes.slipForm}
              render={(props) => (
                <AuthenticationGuard
                  {...props}
                  authorizedComponent={SlipForm}
                  unAuthorizedComponent={UnAuthorized}
                />
              )}
            />

            {/* SHARED FRONTSITE ROUTES - Use ProtectedRoute to block admins */}
            <ProtectedRoute
              exact
              path={FrontSiteRoutes.openingHours}
              component={OpeningHours}
            />
            <ProtectedRoute
              exact
              path={FrontSiteRoutes.faqs}
              component={FrequentlyAskedQuestions}
            />
            <AuthenticationGuard
              exact
              path={FrontSiteRoutes.feedback}
              authorizedComponent={Feedback}
              unAuthorizedComponent={UnAuthorized}
            />
            <ProtectedRoute
              exact
              path={FrontSiteRoutes.specialCollectionDetails}
              component={SpecialCollectionDetails}
            />
            <Route
              exact
              path={FrontSiteRoutes.attendance}
              component={LibraryIdAttendance}
            />

            {/* Self Checkout - Use ProtectedRoute to block admins */}
            {invenioConfig.APP.ENABLE_SELF_CHECKOUT && (
              <ProtectedRoute
                exact
                path={FrontSiteRoutes.selfCheckout}
                component={() => (
                  <AuthenticationGuard
                    authorizedComponent={SelfCheckout}
                    unAuthorizedComponent={UnAuthorized}
                  />
                )}
              />
            )}

            {/* PUBLIC ROUTES - Use regular Route (accessible to everyone including admins) */}
            <Route exact path={FrontSiteRoutes.errors} component={ErrorsPage} />

            {/* Static Pages - Use regular Route (these are typically public) */}
            {staticPagesRoutes.map((route) => (
              <Route key={route} exact path={route} component={StaticPage} />
            ))}

            {this.renderCustomStaticPages()}

            <Overridable id="FrontSite.CustomRoute" />

            {/* 404 - Use regular Route */}
            <Route>
              <NotFound />
            </Route>
          </Switch>
        </Container>
        <ILSFooter />
      </div>
    );
  }
}

FrontSite.propTypes = {
  customStaticPages: PropTypes.func,
};

FrontSite.defaultProps = {
  customStaticPages: () => {},
};
